import { LegislationMetadata } from "@/models/legislation/Legislation";
import { ArvidMetadata } from "@/models/legislation/metadata/Arvid";
import { DomsdatabasenMetadata } from "@/models/legislation/metadata/Domsdatabasen";
import { RetsinformationMetadata } from "@/models/legislation/metadata/Retsinformation";
import { dateValue, parseEurLexTitle } from "@/utils/legislationMetadataUtils";
import { DataSource, normalizeDataSource } from "@/config/datasources";
import { SearchResult } from "@/models/search/SearchResults";

export const getArvidSummary = (result: SearchResult): string | undefined => {
  if (!result.legislation) return undefined;

  const normalizedSource = normalizeDataSource(result.legislation.source);
  if (normalizedSource !== DataSource.Arvid) return undefined;

  const metadata = result.legislation.metadata as ArvidMetadata | undefined;
  if (!metadata || !metadata.summary) return undefined;

  return metadata.summary.trim() || undefined;
};

export const getMetadataDisplayInfo = (
  metadata: LegislationMetadata | undefined,
  source: string | undefined,
  title?: string
): string[] => {
  if (!metadata || !source) return [];

  const parts: string[] = [];
  const normalizedSource = normalizeDataSource(source);

  switch (normalizedSource) {
    case DataSource.Arvid: {
      const arvid = metadata as ArvidMetadata;
      if (arvid.rulingOn) {
        parts.push(`Dato: ${dateValue(arvid.rulingOn)}`);
      }
      if (arvid.journalNumber) {
        parts.push(`Nummer: ${arvid.journalNumber}`);
      }
      break;
    }

    case DataSource.Domsdatabasen: {
      const domsdatabasen = metadata as DomsdatabasenMetadata;
      if (domsdatabasen.documents && domsdatabasen.documents.length > 0) {
        let domDocument = domsdatabasen.documents.find(
          (document) =>
            document.verdictDateTime &&
            document.documentType &&
            (document.documentType === "Dom" ||
              document.documentType.toLowerCase().includes("dom"))
        );

        if (!domDocument) {
          domDocument = domsdatabasen.documents.find(
            (document) => document.verdictDateTime
          );
        }

        if (domDocument?.verdictDateTime) {
          parts.push(`Dato: ${dateValue(domDocument.verdictDateTime)}`);
        }
      }
      if (domsdatabasen.courtCaseNumber) {
        parts.push(`Nummer: ${domsdatabasen.courtCaseNumber}`);
      }
      break;
    }

    case DataSource.Eurlex:
      if (title) {
        const parsed = parseEurLexTitle(title);
        if (parsed.number) {
          parts.push(`Nummer: ${parsed.number}`);
        }
        if (parsed.date) {
          parts.push(`Dato: ${dateValue(parsed.date)}`);
        }
      }
      break;

    case DataSource.Retsinformation: {
      const retsinformation = metadata as RetsinformationMetadata;
      if (retsinformation.signatureDate) {
        parts.push(`Dato: ${dateValue(retsinformation.signatureDate)}`);
      } else if (
        !retsinformation.signatureDate &&
        retsinformation.yearOfIssue
      ) {
        parts.push(`Udgivelsesår: ${retsinformation.yearOfIssue.toString()}`);
      }
      if (retsinformation.regulationNumber)
        parts.push(`Nummer: ${retsinformation.regulationNumber}`);
      break;
    }

    default:
      break;
  }

  return parts;
};
